import * as React from 'react';
import SvgEditService from '../../../services/svg-edit-service';
import styles from './TagEditorTextbox.module.scss';
import Editor, { Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';

interface ITagEditorTextboxProps {
    tagAttrib: string;
    lang: string;
}

interface ITagEditorTextboxState {
    tagCode: string;
}

const monacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    minimap: {
        enabled: false,
    },
    lineNumbers: 'off',
    glyphMargin: false,
    folding: false,
}

export default class PDTagEditorTextbox extends React.Component<ITagEditorTextboxProps, ITagEditorTextboxState> {
    state = {
        tagCode: ""
    }

    private svgEditService = SvgEditService.Get(this, () => this.onSelectionChanged());

    private editorInstance: any;

    public componentDidMount() {
        this.onSelectionChanged(); // Trigger on first load
    }

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
    }

    public onSelectionChanged = () => {
        const els = this.svgEditService.selectedElements;

        if (els.length === 1) {
            this.setState({
                tagCode: (els[0].getAttribute(this.props.tagAttrib) ?? "").replace(/&#10;/gi, "\n")
            });
        } else {
            this.setState({
                tagCode: ""
            });
        }
    }

    private updateTagCode = (newTagCode: string) => {
        let attrValue;
        if (!newTagCode || newTagCode.length === 0) {
            newTagCode = "";
            attrValue = null;
        } else {
            // Escape newlines to not break the SVG attribute.
            attrValue = newTagCode.replace(/(?:\r\n|\r|\n)/gi, "&#10;");
        }

        this.svgEditService.canvas.changeSelectedAttribute(this.props.tagAttrib, attrValue, null);
        this.setState({ tagCode: newTagCode });
        this.svgEditService.ElementStateHasChanged();
    }

    public insertTag(tagToInsert: string, overwrite: boolean = false) {
        const editorModel = this.editorInstance.getModel();
        let newTag = tagToInsert;

        if (!overwrite) {
            const cursorPosition = this.editorInstance.getPosition();
            const stringOffset = editorModel.getOffsetAt(cursorPosition);

            const prefix = this.state.tagCode.slice(0, stringOffset);
            const suffix = this.state.tagCode.slice(stringOffset)

            newTag = prefix + newTag + suffix;
        }

        this.updateTagCode(newTag);
    }

    private handleEditorWillMount = (monaco: Monaco) => {
        monaco.languages.register({
            id: 'zaa_tagscript'
        });

        // See https://microsoft.github.io/monaco-editor/monarch.html for info
        monaco.languages.setMonarchTokensProvider('zaa_tagscript', {
            brackets: [
                { open: "${{", close: "}}", token: "delimiter.curly" },
                { open: "$<<", close: ">>", token: "delimiter.angle" },
                { open: "(", close: ")", token: "delimiter.parenthesis" },
            ],
            tokenizer: {
                root: [
                    [/(\$(?:{{|<<))/, { token: 'keyword', bracket: '@open', next: '@innertag' }],
                    [/(}}|>>)/, { token: 'keyword', bracket: '@close', next: '@pop' }]
                ],

                innertag: [
                    [/\w+/, "keyword"],
                    [/\(/, { token: 'keyword', bracket: '@open', next: '@arguments' }],
                    [/&[\w-]+(\(\))?/, "type"],
                    [/(}}|>>)/, { token: 'keyword', bracket: '@close', next: '@pop' }]
                ],

                arguments: [
                    [/\)/, { token: 'keyword', bracket: '@close', next: '@pop' }],
                    [/(\$(?:{{|<<))/, { token: 'keyword', bracket: '@open', next: '@innertag' }],
                    [/\s*[^,\)]+(?=\s*[,\)])/, "string"],
                ]
            }
        });
    }

    private handleEditorDidMount = (editor: any, monaco: Monaco) => {
        this.editorInstance = editor;
        (window as any).edInst = editor;
    }

    public render() {
        return (
            <>
                <div className={styles.tagEditor}>
                    <Editor 
                        defaultLanguage="zaa_tagscript"
                        options={monacoOptions}
                        beforeMount={this.handleEditorWillMount}
                        onMount={this.handleEditorDidMount}
                        theme="light"
                        defaultValue={this.state.tagCode}
                        value={this.state.tagCode}
                        onChange={value => this.updateTagCode(value)} />
                </div>
            </>
        );
    }
}
